// src/tabs/ProductsTab.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Chip,
  Card,
  CardContent,
  Fade,
  Tooltip,
  InputAdornment,
} from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
  Close,
  DeleteOutlined,
  EditOutlined,
  KeyboardArrowDownRounded,
  SearchOutlined,
  StorefrontOutlined,
  TravelExploreOutlined,
  TuneRounded,
  VisibilityOutlined,
} from "@mui/icons-material";
import PropTypes from "prop-types";
import debounce from "lodash.debounce";
import axiosInstance from "../axiosInstance";
import TenantAutocomplete from "./TenantAutocomplete";
import { isAdminUser, isFullAdmin } from "../config/adminUsers";
import { getUserEmail } from "../utils/getUserEmail";

const PRODUCTS_PER_PAGE = 10;

const mapApiProduct = (p) => {
  const firstVariant = p.Variants?.[0] ?? {};
  const variants = (p.Variants ?? []).map((v) => ({
    id: v.Id,
    sku: v.Sku,
    title: v.Title,
    price: v.Price,
    displaySize: v.DisplaySize,
    displayColor: v.DisplayColor,
    stock: v.StockCount,
    imageUrl: v.ImageUrl || firstVariant.ImageUrl || "",
    link: v.VariantUrl || "",
    raw: v,
  }));

  let price = p.Price ?? p.price;
  if (!price || price === 0 || price === "0") {
    price = firstVariant.Price ?? firstVariant.price ?? "N/A";
  }

  return {
    id: p.Id ?? p.id,
    documentId: p.DocumentId ?? p.documentId,
    title: p.Title ?? p.title,
    imageUrl: p.Images?.[0] || firstVariant.ImageUrl || "",
    link: p.Link ?? firstVariant.VariantUrl ?? "",
    industry: p.Industry ?? "",
    productType: p.ProductType ?? "",
    price,
    variants,
    scraped: !!p.ProductPageData && p.ProductPageData !== "" && p.ProductPageData !== p.BodyHtml,
    raw: p,
  };
};

const variantColors = [
  '#e91e63', '#9c27b0', '#673ab7', '#3f51b5', '#2196f3',
  '#00bcd4', '#009688', '#4caf50', '#ff9800', '#ff5722',
  '#795548', '#607d8b', '#f44336', '#e040fb', '#00e676'
];

const variantBackgrounds = [
  'rgba(233, 30, 99, 0.08)',
  'rgba(156, 39, 176, 0.08)',
  'rgba(103, 58, 183, 0.08)',
  'rgba(63, 81, 181, 0.08)',
  'rgba(33, 150, 243, 0.08)',
  'rgba(0, 188, 212, 0.08)',
  'rgba(0, 150, 136, 0.08)',
  'rgba(76, 175, 80, 0.08)',
  'rgba(255, 152, 0, 0.08)',
  'rgba(255, 87, 34, 0.08)',
  'rgba(121, 85, 72, 0.08)',
  'rgba(96, 125, 139, 0.08)',
  'rgba(244, 67, 54, 0.08)',
  'rgba(224, 64, 251, 0.08)',
  'rgba(0, 230, 118, 0.08)'
];

const ColoredJSONVariants = React.memo(({ data, searchTerm = "" }) => {
  const highlight = (value) => {
    const text = String(value);
    if (!searchTerm) return text;
    const normalized = text.toLowerCase();
    const parts = [];
    let start = 0;
    let match = normalized.indexOf(searchTerm);
    while (match !== -1) {
      parts.push(text.slice(start, match));
      parts.push(
        <mark key={match} data-json-match style={{ backgroundColor: "#ffd687", color: "#202531", borderRadius: 3 }}>
          {text.slice(match, match + searchTerm.length)}
        </mark>
      );
      start = match + searchTerm.length;
      match = normalized.indexOf(searchTerm, start);
    }
    parts.push(text.slice(start));
    return parts;
  };

  const renderJSON = (obj, depth = 0, variantIndex = null) => {
    const indent = '  '.repeat(depth);

    if (obj === null) return <span style={{ color: '#999' }}>{highlight("null")}</span>;
    if (obj === undefined) return <span style={{ color: '#999' }}>{highlight("undefined")}</span>;

    if (typeof obj !== 'object') {
      if (typeof obj === 'string') {
        return <span style={{ color: variantIndex !== null ? variantColors[variantIndex % variantColors.length] : '#d32f2f' }}>{highlight(`"${obj}"`)}</span>;
      }
      if (typeof obj === 'number') {
        return <span style={{ color: variantIndex !== null ? variantColors[variantIndex % variantColors.length] : '#1976d2' }}>{highlight(obj)}</span>;
      }
      if (typeof obj === 'boolean') {
        return <span style={{ color: variantIndex !== null ? variantColors[variantIndex % variantColors.length] : '#388e3c' }}>{highlight(obj)}</span>;
      }
      return <span>{String(obj)}</span>;
    }

    if (Array.isArray(obj)) {
      if (obj.length === 0) return <span>[]</span>;

      const isVariantsArray = obj.length > 0 && obj[0] && (obj[0].Id || obj[0].Sku || obj[0].VariantUrl);

      return (
        <>
          <span style={{ color: variantIndex !== null ? variantColors[variantIndex % variantColors.length] : 'inherit' }}>[</span>
          {'\n'}
          {obj.map((item, index) => {
            const itemVariantIndex = isVariantsArray ? index : variantIndex;
            const bgColor = isVariantsArray ? variantBackgrounds[index % variantBackgrounds.length] : 'transparent';

            return (
              <span key={index} style={{
                backgroundColor: bgColor,
                display: 'block',
                paddingTop: isVariantsArray ? '4px' : '0',
                paddingBottom: isVariantsArray ? '4px' : '0',
                marginTop: isVariantsArray && index > 0 ? '2px' : '0',
                borderLeft: isVariantsArray ? `3px solid ${variantColors[index % variantColors.length]}` : 'none',
                paddingLeft: isVariantsArray ? '8px' : '0'
              }}>
                {indent}
                {renderJSON(item, depth + 1, itemVariantIndex)}
                {index < obj.length - 1 ? <span style={{ color: variantIndex !== null ? variantColors[variantIndex % variantColors.length] : 'inherit' }}>,</span> : ''}
                {'\n'}
              </span>
            );
          })}
          {indent}
          <span style={{ color: variantIndex !== null ? variantColors[variantIndex % variantColors.length] : 'inherit' }}>]</span>
        </>
      );
    }

    const entries = Object.entries(obj);
    if (entries.length === 0) return <span>{'{}'}</span>;

    return (
      <>
        <span style={{ color: variantIndex !== null ? variantColors[variantIndex % variantColors.length] : 'inherit' }}>{'{'}</span>
        {'\n'}
        {entries.map(([key, value], index) => {
          const isHighlighted = key === "Id" || key === "Title";
          return (
            <span key={key}>
              {indent}
              <span style={{
                color: variantIndex !== null ? variantColors[variantIndex % variantColors.length] : (isHighlighted ? '#000' : '#6a1b9a'),
                fontWeight: isHighlighted ? 800 : 600,
                backgroundColor: isHighlighted ? 'rgba(255, 235, 59, 0.4)' : 'transparent',
                padding: isHighlighted ? '0 2px' : '0',
                borderRadius: isHighlighted ? '2px' : '0'
              }}>{highlight(`"${key}"`)}</span>
              <span style={{ color: variantIndex !== null ? variantColors[variantIndex % variantColors.length] : 'inherit' }}>: </span>
              {renderJSON(value, depth + 1, variantIndex)}
              {index < entries.length - 1 ? <span style={{ color: variantIndex !== null ? variantColors[variantIndex % variantColors.length] : 'inherit' }}>,</span> : ''}
              {'\n'}
            </span>
          );
        })}
        {indent}
        <span style={{ color: variantIndex !== null ? variantColors[variantIndex % variantColors.length] : 'inherit' }}>{'}'}</span>
      </>
    );
  };

  return <>{renderJSON(data)}</>;
});

const CollapsibleJSON = ({ data, searchTerm = "", depth = 0 }) => {
  const [collapsed, setCollapsed] = React.useState({});

  const toggle = (key) => {
    setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const highlight = (text) => {
    if (!searchTerm.trim()) return text;
    const parts = String(text).split(new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'));
    return parts.map((part, i) =>
      part.toLowerCase() === searchTerm.toLowerCase()
        ? <mark key={i} style={{ backgroundColor: "rgba(249, 115, 22, 0.3)", color: "#202531", borderRadius: "2px", padding: "0 2px" }}>{part}</mark>
        : part
    );
  };

  const styles = {
    key: { color: "#6a1b9a", fontWeight: 600 },
    str: { color: "#2e7d32" },
    bool: { color: "#e65100" },
    null: { color: "#9ca3af", fontStyle: "italic" },
    num: { color: "#1565c0" },
    bracket: { color: "#374151" },
    toggle: { color: "#f97316", cursor: "pointer", userSelect: "none", fontWeight: 700, fontSize: "0.7rem", marginRight: 4 },
  };

  const indent = (level) => "  ".repeat(level);

  const renderValue = (value, path, level) => {
    if (value === null) return <span style={styles.null}>{highlight("null")}</span>;
    if (typeof value === "boolean") return <span style={styles.bool}>{highlight(String(value))}</span>;
    if (typeof value === "number") return <span style={styles.num}>{highlight(String(value))}</span>;
    if (typeof value === "string") return <span style={styles.str}>{highlight(`"${value}"`)}</span>;

    if (Array.isArray(value)) {
      if (value.length === 0) return <span style={styles.bracket}>[]</span>;
      const isCollapsed = collapsed[path];
      return (
        <>
          <span style={styles.toggle} onClick={() => toggle(path)}>{isCollapsed ? "▶" : "▼"}</span>
          <span style={styles.bracket}>[</span>
          <span style={{ color: "#9ca3af", fontSize: "0.7rem" }}> {value.length} items</span>
          {isCollapsed ? (
            <span style={styles.bracket}>{" ... ]"}</span>
          ) : (
            <>
              {'\n'}
              {value.map((item, i) => (
                <span key={i}>
                  {indent(level + 1)}
                  {renderValue(item, `${path}[${i}]`, level + 1)}
                  {i < value.length - 1 ? "," : ""}
                  {'\n'}
                </span>
              ))}
              {indent(level)}<span style={styles.bracket}>]</span>
            </>
          )}
        </>
      );
    }

    if (typeof value === "object") {
      const entries = Object.entries(value);
      if (entries.length === 0) return <span style={styles.bracket}>{"{}"}</span>;
      const isCollapsed = collapsed[path];
      return (
        <>
          {depth > 0 || level > 0 ? <span style={styles.toggle} onClick={() => toggle(path)}>{isCollapsed ? "▶" : "▼"}</span> : null}
          <span style={styles.bracket}>{"{"}</span>
          {isCollapsed ? (
            <span style={styles.bracket}>{" ... }"}</span>
          ) : (
            <>
              {'\n'}
              {entries.map(([k, v], i) => (
                <span key={k}>
                  {indent(level + 1)}
                  <span style={styles.key}>{highlight(`"${k}"`)}</span>
                  <span style={styles.bracket}>: </span>
                  {renderValue(v, `${path}.${k}`, level + 1)}
                  {i < entries.length - 1 ? "," : ""}
                  {'\n'}
                </span>
              ))}
              {indent(level)}<span style={styles.bracket}>{"}"}</span>
            </>
          )}
        </>
      );
    }

    return <span>{String(value)}</span>;
  };

  return renderValue(data, "root", depth);
};

const DebouncedSearchInput = React.memo(({ open, onSearch, label = "Search variants", placeholder = "Search variant content", clearLabel = "Clear variant search" }) => {
  const [value, setValue] = useState("");

  useEffect(() => {
    if (!open) {
      setValue("");
      onSearch("");
    }
  }, [open, onSearch]);

  useEffect(() => {
    if (!open) return;
    const timeoutId = setTimeout(() => onSearch(value.trim().toLowerCase()), 100);
    return () => clearTimeout(timeoutId);
  }, [value, open, onSearch]);

  return (
    <TextField
      size="small"
      label={label}
      placeholder={placeholder}
      value={value}
      onChange={(event) => setValue(event.target.value)}
      sx={{ width: { xs: "100%", sm: 280 }, maxWidth: "100%", "& .MuiOutlinedInput-root": { borderRadius: "20px" } }}
      InputProps={{
        startAdornment: <InputAdornment position="start"><SearchOutlined fontSize="small" /></InputAdornment>,
        endAdornment: value ? (
          <InputAdornment position="end">
            <IconButton size="small" aria-label={clearLabel} onClick={() => { setValue(""); onSearch(""); }}>
              <Close fontSize="small" />
            </IconButton>
          </InputAdornment>
        ) : null,
      }}
    />
  );
});

const ProductJSONPanel = React.memo(({ product, open, fullView = false, fillHeight = false }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [matchCount, setMatchCount] = useState(0);
  const contentRef = useRef(null);

  useEffect(() => {
    const content = contentRef.current;
    if (!content) return;
    const matches = content.querySelectorAll("[data-json-match]");
    setMatchCount(matches.length);
    if (searchTerm && matches.length) {
      content.scrollTop += matches[0].getBoundingClientRect().top - content.getBoundingClientRect().top - 16;
    } else {
      content.scrollTop = 0;
    }
  }, [searchTerm, product]);

  return (
    <Card
      component="section"
      aria-label="Product JSON"
      elevation={0}
      sx={{
        display: "flex",
        flexDirection: "column",
        position: fillHeight ? { xs: "relative", lg: "absolute" } : "relative",
        inset: fillHeight ? { lg: 0 } : undefined,
        minHeight: 0,
        borderRadius: "22px",
        overflow: "hidden",
        border: "1px solid #efddcf",
        boxShadow: "0 18px 38px rgba(236, 123, 55, 0.07)",
      }}
    >
      <Box sx={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        flexWrap: "wrap",
        flexShrink: 0,
        gap: 1.5,
        p: 1.75,
        background: "linear-gradient(135deg, #fdf5ee, #faece0)",
        borderBottom: "1px solid #efddcf",
      }}>
        <Typography variant="subtitle2" sx={{ display: "flex", alignItems: "center", minHeight: 40, fontWeight: 700, color: "#513725", lineHeight: 1.4, minWidth: 0 }}>
          Product JSON
          {product?.id != null && (
            <Box component="span" sx={{ ml: 1, fontWeight: 500, color: "#6d4c36", overflowWrap: "anywhere" }}>
              ID: {product.id}
            </Box>
          )}
        </Typography>
        <Box sx={{ ml: "auto", width: { xs: "100%", sm: "auto" }, maxWidth: "100%" }}>
          <DebouncedSearchInput
            open={open}
            onSearch={setSearchTerm}
            label="Search product JSON"
            placeholder="Search keys or values"
            clearLabel="Clear product JSON search"
          />
          {searchTerm && (
            <Typography variant="caption" role="status" sx={{ display: "block", textAlign: "right", color: "#513725", mt: 0.5 }}>
              {matchCount ? `${matchCount} ${matchCount === 1 ? "match" : "matches"}` : "No matches"}
            </Typography>
          )}
        </Box>
      </Box>
      <Box component="pre" ref={contentRef} sx={{
        flex: "1 1 auto",
        minHeight: 0,
        backgroundColor: "#fffaf7",
        p: fullView ? 2 : 1.75,
        fontFamily: 'Consolas, Monaco, "Courier New", monospace',
        fontSize: "0.75rem",
        whiteSpace: "pre-wrap",
        wordBreak: "break-all",
        maxHeight: fillHeight ? { xs: "70vh", lg: "none" } : "70vh",
        overflow: "auto",
        m: 0,
      }}>
        <ColoredJSONVariants data={product?.raw} searchTerm={searchTerm} />
      </Box>
    </Card>
  );
});

const ProductsTab = ({ tenants: externalTenants = [], selectedTenant, setSelectedTenant }) => {
  const isAdmin = isAdminUser(getUserEmail());
  const hasFullAdmin = isFullAdmin(getUserEmail());
  const [tenantOptions, setTenantOptions] = useState(externalTenants);
  const [loadingTenants, setLoadingTenants] = useState(false);

  useEffect(() => { if (externalTenants.length) setTenantOptions(externalTenants); }, [externalTenants]);
  useEffect(() => {
    if (externalTenants.length) return;

    (async () => {
      setLoadingTenants(true);
      try {
        const { data } = await axiosInstance.get("/tenant/alltenants");
        setTenantOptions(data ?? []);
      } catch {
      } finally {
        setLoadingTenants(false);
      }
    })();
  }, [externalTenants]);

  const [tenantId, setTenantId] = useState(selectedTenant ?? "");
  useEffect(() => { if (selectedTenant !== undefined) setTenantId(selectedTenant); }, [selectedTenant]);
  const notifyParent = typeof setSelectedTenant === "function" ? setSelectedTenant : () => { };
  const currentTenant = tenantOptions.find((t) => t.id === tenantId) || null;
  const productIndexName = currentTenant?.elasticIndexName ?? currentTenant?.tenantAiProfile?.elasticIndexName ?? "";

  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loadingProducts, setLoadingProducts] = useState(false);

  const [openJson, setOpenJson] = useState(false);
  const [jsonProduct, setJsonProduct] = useState(null);
  const [variantSearchTerm, setVariantSearchTerm] = useState("");
  const variantSearchIndex = useMemo(() => (jsonProduct?.variants ?? []).map((variant) => ({
    variant,
    content: JSON.stringify(variant.raw ?? variant).toLowerCase(),
  })), [jsonProduct]);
  const filteredVariants = useMemo(() => {
    return variantSearchTerm
      ? variantSearchIndex.filter(({ content }) => content.includes(variantSearchTerm)).map(({ variant }) => variant)
      : jsonProduct?.variants ?? [];
  }, [jsonProduct, variantSearchIndex, variantSearchTerm]);
  const matchingVariant = filteredVariants.length === 1 ? filteredVariants[0] : null;
  const [openVariantJson, setOpenVariantJson] = useState(false);
  const [variantJson, setVariantJson] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [deleteConfirmProduct, setDeleteConfirmProduct] = useState(null);
  const [deletingProductId, setDeletingProductId] = useState(null);
  const [scrapedDataOpen, setScrapedDataOpen] = useState(false);
  const [scrapedDataJson, setScrapedDataJson] = useState(null);
  const [scrapedDataLoading, setScrapedDataLoading] = useState(null);
  const [scrapedDataSearch, setScrapedDataSearch] = useState("");
  const [scrapedDataProductId, setScrapedDataProductId] = useState("");
  const [scrapedDataProductName, setScrapedDataProductName] = useState("");
  const [scrapedDataView, setScrapedDataView] = useState("compact");
  const [editProduct, setEditProduct] = useState(null);
  const [editFields, setEditFields] = useState("{\n  \n}");
  const [editFieldsError, setEditFieldsError] = useState("");
  const [savingProduct, setSavingProduct] = useState(false);
  const [editFeedback, setEditFeedback] = useState(null);

  const handleDeleteProduct = async (productId) => {
    if (!tenantId || !productId) return;
    setDeletingProductId(productId);
    try {
      await axiosInstance.delete(`/product/${tenantId}/${productId}`);
      setProducts((prev) => prev.filter((p) => p.id !== productId));
      setTotal((prev) => Math.max(0, prev - 1));
    } catch (error) {
      console.error("Error deleting product:", error);
    } finally {
      setDeletingProductId(null);
      setDeleteConfirmProduct(null);
    }
  };

  const handleViewScrapedData = async (productId, productName) => {
    if (!tenantId || !productId) return;
    setScrapedDataLoading(productId);
    setScrapedDataProductId(productId);
    setScrapedDataProductName(productName || "");
    try {
      const { data } = await axiosInstance.get(`/product/scraped/${tenantId}/${productId}`);
      setScrapedDataJson(data);
      setScrapedDataSearch("");
      setScrapedDataOpen(true);
    } catch (error) {
      const errData = error.response?.data;
      const message = errData?.message || "Failed to fetch scraped data.";
      setScrapedDataJson({ _error: message });
      setScrapedDataOpen(true);
    } finally {
      setScrapedDataLoading(null);
    }
  };

  const handleOpenEditProduct = (product) => {
    setEditProduct(product);
    setEditFields(JSON.stringify(product.raw || {}, null, 2));
    setEditFieldsError("");
    setEditFeedback(null);
  };

  const handleSaveProduct = async () => {
    if (!tenantId || !editProduct) return;
    let parsed;
    try {
      parsed = JSON.parse(editFields);
    } catch {
      setEditFieldsError("Invalid JSON. Please enter a valid JSON object.");
      return;
    }
    if (typeof parsed !== "object" || Array.isArray(parsed) || parsed === null) {
      setEditFieldsError("Must be a JSON object (not array or null).");
      return;
    }
    if (Object.keys(parsed).length === 0) {
      setEditFieldsError("Provide at least one field to update.");
      return;
    }
    setEditFieldsError("");
    setSavingProduct(true);
    setEditFeedback(null);
    try {
      await axiosInstance.patch(`/product/${tenantId}/${editProduct.id}`, parsed);
      setEditFeedback({ type: "success", message: "Product updated successfully." });
      fetchProducts(tenantId, query, page);
    } catch (error) {
      const msg = error.response?.data?.message || error.response?.data || "Failed to update product.";
      setEditFeedback({ type: "error", message: String(msg) });
    } finally {
      setSavingProduct(false);
    }
  };

  const fetchProducts = async (tid, q, p) => {
    if (!tid) return;
    setLoadingProducts(true);
    try {
      const { data } = await axiosInstance.post(
        `/product/${tid}/search`,
        { query: q, productsPerPage: PRODUCTS_PER_PAGE, page: p, tenantId: tid },
        { headers: { TenantId: tid } }
      );
      const raw = data.products ?? data.items ?? [];
      setProducts(raw.map(mapApiProduct));
      setTotal(data.totalProducts ?? data.total ?? raw.length);
    } catch {
      setProducts([]);
      setTotal(0);
    } finally {
      setLoadingProducts(false);
    }
  };

  const debouncedFetch = useMemo(() => debounce(fetchProducts, 400), []);
  useEffect(() => () => debouncedFetch.cancel(), [debouncedFetch]);

  useEffect(() => {
    if (!tenantId) return;

    setQuery("");
    setPage(1);
  }, [tenantId]);

  useEffect(() => {
    if (!tenantId) return;

    if (query === "" && page === 1) {
      fetchProducts(tenantId, "", 1);
      return;
    }

    debouncedFetch(tenantId, query, page);
  }, [query, page, tenantId, debouncedFetch]);

  const start = (page - 1) * PRODUCTS_PER_PAGE + 1;
  const end = Math.min(page * PRODUCTS_PER_PAGE, total);
  const totalPages = Math.ceil(total / PRODUCTS_PER_PAGE);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const renderPreviewImage = (src, alt, size = 42, radius = 12) => (
    src ? (
      <Box
        component="img"
        src={src}
        alt={alt}
        sx={{
          width: size,
          height: size,
          objectFit: "cover",
          borderRadius: `${radius}px`,
          border: "1px solid rgba(255, 132, 76, 0.12)",
          boxShadow: "0 10px 24px rgba(236, 123, 55, 0.12)",
          backgroundColor: "#fff",
        }}
      />
    ) : (
      <Box
        sx={{
          width: size,
          height: size,
          borderRadius: `${radius}px`,
          background: "linear-gradient(135deg, rgba(255, 163, 112, 0.22), rgba(255, 122, 46, 0.08))",
          border: "1px solid rgba(255, 132, 76, 0.16)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#f97316",
        }}
      >
        <StorefrontOutlined sx={{ fontSize: size * 0.46 }} />
      </Box>
    )
  );

  const paginationControls = totalPages > 1 ? (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
      }}
    >
      <IconButton
        size="small"
        onClick={() => setPage(page - 1)}
        disabled={page === 1}
        sx={{
          width: 42,
          height: 42,
          borderRadius: "14px",
          bgcolor: "rgba(255,255,255,0.86)",
          border: "1px solid rgba(255, 132, 76, 0.14)",
          boxShadow: "0 10px 22px rgba(236, 123, 55, 0.08)",
          color: "#6b7280",
          "&:hover": { bgcolor: "#fff", color: "#f97316" },
          "&.Mui-disabled": {
            bgcolor: "rgba(255,255,255,0.62)",
            color: "rgba(107, 114, 128, 0.35)",
          },
        }}
      >
        <ChevronLeft />
      </IconButton>
      <Typography
        variant="body2"
        sx={{
          minWidth: 92,
          textAlign: "center",
          color: "#4b5563",
          fontWeight: 600,
        }}
      >
        Page {page} of {totalPages}
      </Typography>
      <IconButton
        size="small"
        onClick={() => setPage(page + 1)}
        disabled={page === totalPages}
        sx={{
          width: 42,
          height: 42,
          borderRadius: "14px",
          bgcolor: "rgba(255,255,255,0.86)",
          border: "1px solid rgba(255, 132, 76, 0.14)",
          boxShadow: "0 10px 22px rgba(236, 123, 55, 0.08)",
          color: "#6b7280",
          "&:hover": { bgcolor: "#fff", color: "#f97316" },
          "&.Mui-disabled": {
            bgcolor: "rgba(255,255,255,0.62)",
            color: "rgba(107, 114, 128, 0.35)",
          },
        }}
      >
        <ChevronRight />
      </IconButton>
    </Box>
  ) : null;

  return (
    <Box
      sx={{
        maxWidth: 1400,
        mx: "auto",
        px: { xs: 1, md: 2 },
        py: { xs: 1, md: 2 },
      }}
    >
      <Box
        sx={{
          borderRadius: { xs: 4, md: 6 },
          background: "radial-gradient(circle at top center, rgba(255, 191, 154, 0.28), rgba(255,255,255,0.94) 42%), linear-gradient(180deg, rgba(255, 248, 243, 0.96), rgba(255, 255, 255, 0.92))",
          boxShadow: "0 24px 60px rgba(236, 123, 55, 0.10)",
          border: "1px solid rgba(255, 132, 76, 0.10)",
          p: { xs: 2, md: 4 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 2.25,
            mb: 3.5,
          }}
        >
          <Box
            sx={{
              width: { xs: 56, md: 64 },
              height: { xs: 56, md: 64 },
              borderRadius: "20px",
              background: "linear-gradient(180deg, #ffb58d 0%, #ff8b47 100%)",
              boxShadow: "0 18px 38px rgba(255, 139, 71, 0.30)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              flexShrink: 0,
            }}
          >
            <StorefrontOutlined sx={{ fontSize: 30 }} />
          </Box>
          <Box>
            <Typography
              variant="h3"
              sx={{
                fontSize: { xs: "2rem", md: "2.55rem" },
                lineHeight: 1.05,
                fontWeight: 800,
                color: "#202531",
                letterSpacing: "-0.03em",
                mb: 0.8,
              }}
            >
              Products
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "#6b7280",
                fontWeight: 500,
              }}
            >
              Browse and manage all products across tenants.
            </Typography>
          </Box>
        </Box>

        <Card
          elevation={0}
          sx={{
            mb: 3.25,
            borderRadius: "24px",
            border: "1px solid rgba(255, 132, 76, 0.10)",
            backgroundColor: "rgba(255,255,255,0.88)",
            boxShadow: "0 20px 44px rgba(236, 123, 55, 0.08)",
            overflow: "visible",
          }}
        >
          <CardContent sx={{ p: { xs: 2, md: 3 }, "&:last-child": { pb: { xs: 2, md: 3 } } }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", lg: "1.15fr 1.1fr auto" },
                gap: 2,
                alignItems: "end",
              }}
            >
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    color: "#6b7280",
                    fontWeight: 700,
                    mb: 0.9,
                    px: 0.5,
                  }}
                >
                  Select Tenant
                </Typography>
                <TenantAutocomplete
                  tenants={tenantOptions}
                  loading={loadingTenants}
                  value={currentTenant}
                  onChange={(_, v) => {
                    const id = v?.id ?? "";
                    setTenantId(id);
                    notifyParent(id);
                    setProducts([]);
                    setTotal(0);
                  }}
                  placeholder="Choose a tenant"
                  popupIcon={<KeyboardArrowDownRounded sx={{ color: "#6b7280" }} />}
                />
              </Box>

              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    color: "transparent",
                    fontWeight: 700,
                    mb: 0.9,
                    px: 0.5,
                    userSelect: "none",
                  }}
                >
                  Search
                </Typography>
                <TextField
                  fullWidth
                  placeholder="Search products..."
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setPage(1);
                  }}
                  size="small"
                  disabled={!tenantId}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      minHeight: 56,
                      borderRadius: "16px",
                      backgroundColor: "#fff",
                      "& fieldset": { borderColor: "rgba(15, 23, 42, 0.08)" },
                      "&:hover fieldset": { borderColor: "rgba(249, 115, 22, 0.35)" },
                      "&.Mui-focused fieldset": { borderColor: "#fb923c", borderWidth: "1px" },
                    },
                    "& .MuiOutlinedInput-input": {
                      color: "#202531",
                      fontWeight: 500,
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchOutlined sx={{ color: "#6b7280" }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              <Tooltip title="More filters coming soon" arrow>
                <span>
                  <IconButton
                    disabled={!tenantId}
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: "16px",
                      border: "1px solid rgba(15, 23, 42, 0.08)",
                      backgroundColor: "#fff",
                      boxShadow: "0 14px 30px rgba(236, 123, 55, 0.08)",
                      color: "#4b5563",
                      "&:hover": {
                        backgroundColor: "#fff7f1",
                        color: "#f97316",
                      },
                      "&.Mui-disabled": {
                        backgroundColor: "rgba(255,255,255,0.8)",
                        color: "rgba(75, 85, 99, 0.35)",
                      },
                    }}
                  >
                    <TuneRounded />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </CardContent>
        </Card>

      {loadingProducts && (
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" mt={5} mb={5}>
          <CircularProgress size={48} thickness={4} sx={{ color: "#f97316" }} />
          <Typography variant="body2" sx={{ color: "#6b7280", mt: 1.5, fontWeight: 600 }}>
            Loading products...
          </Typography>
        </Box>
      )}

      {!loadingProducts && products.length > 0 && (
        <Fade in={true}>
          <Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 1.5,
                flexWrap: "wrap",
                mb: 1.75,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", columnGap: 6, rowGap: 1.5, flexWrap: "wrap", minWidth: 0 }}>
                <Typography variant="body2" sx={{ color: "#4b5563", fontWeight: 600 }}>
                  Showing {start}-{end} of {total} products
                </Typography>
                <Typography variant="body2" sx={{ color: "#6b7280", overflowWrap: "anywhere", minWidth: 0 }}>
                  Index: <Box component="span" sx={{ fontWeight: 600 }}>{productIndexName || "--"}</Box>
                </Typography>
              </Box>
              {paginationControls}
            </Box>

            <TableContainer
              component={Paper}
              elevation={0}
              role="region"
              aria-label="Products table"
              tabIndex={0}
              sx={{
                borderRadius: "22px",
                overflow: "hidden",
                overflowX: "auto",
                border: "1px solid rgba(255, 132, 76, 0.10)",
                boxShadow: "0 24px 60px rgba(236, 123, 55, 0.08)",
                backgroundColor: "rgba(255,255,255,0.92)",
              }}
            >
              <Table
                size="small"
                sx={{
                  tableLayout: "fixed",
                  minWidth: 1200,
                  "& th": {
                    background: "linear-gradient(180deg, rgba(255, 244, 238, 0.98), rgba(255, 247, 243, 0.98))",
                    color: "#f97316",
                    fontWeight: 800,
                    py: 2,
                    borderBottom: "1px solid rgba(255, 132, 76, 0.10)",
                    fontSize: "0.75rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                  },
                  "& td": {
                    py: 1.4,
                    borderBottom: "1px solid rgba(238, 240, 244, 1)",
                  },
                  "& tbody tr:hover": {
                    backgroundColor: "rgba(255, 247, 243, 0.72)",
                  },
                  "& th:last-child, & td:last-child": {
                    position: "sticky",
                    right: 0,
                    zIndex: 1,
                    backgroundColor: "#fff",
                    boxShadow: "-8px 0 16px rgba(15, 23, 42, 0.06)",
                  },
                  "& th:last-child": {
                    zIndex: 2,
                    background: "linear-gradient(180deg, #fff4ee, #fff7f3)",
                  },
                  "& tbody tr:hover td:last-child": {
                    backgroundColor: "#fff7f3",
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: "26%" }}>Product</TableCell>
                    <TableCell sx={{ width: "12%" }}>Variants</TableCell>
                    <TableCell sx={{ width: "14%" }}>ID</TableCell>
                    <TableCell sx={{ width: "14%" }}>Document ID</TableCell>
                    <TableCell sx={{ width: "10%" }}>Type</TableCell>
                    <TableCell align="center" sx={{ width: "10%" }}>Scrape valid</TableCell>
                    <TableCell align="center" sx={{ width: { xs: 116, lg: 208 } }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {products.map((p) => {
                    const max = 3;
                    const disp = p.variants.slice(0, max);
                    const extra = p.variants.length - max;
                    return (
                      <TableRow key={p.id} sx={{ "&:last-child td": { border: 0 } }}>
                        <TableCell>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                              minWidth: 0,
                            }}
                          >
                            {renderPreviewImage(p.imageUrl, p.title, 44, 10)}
                            <Box sx={{ minWidth: 0 }}>
                              <Typography
                                variant="body2"
                                onClick={() => { setJsonProduct(p); setVariantSearchTerm(""); setOpenJson(true); setTabValue(0); }}
                                sx={{
                                  textDecoration: "none",
                                  color: "#202531",
                                  fontWeight: 700,
                                  fontSize: "1rem",
                                  lineHeight: 1.3,
                                  display: "-webkit-box",
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                  overflow: "hidden",
                                  cursor: "pointer",
                                  "&:hover": { color: "#f97316" },
                                }}
                              >
                                {p.title}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.9, flexWrap: "wrap" }}>
                            {disp.map((v, i) => (
                              <Tooltip key={i} title={v.sku || v.title || "Variant"} arrow>
                                <Box
                                  component={v.link ? "a" : "div"}
                                  href={v.link || undefined}
                                  target={v.link ? "_blank" : undefined}
                                  rel={v.link ? "noopener noreferrer" : undefined}
                                  sx={{
                                    textDecoration: "none",
                                    display: "inline-flex",
                                  }}
                                >
                                  {renderPreviewImage(v.imageUrl, v.sku || v.title || `Variant ${i + 1}`, 34, 8)}
                                </Box>
                              </Tooltip>
                            ))}
                            {extra > 0 && (
                              <Box
                                sx={{
                                  minWidth: 34,
                                  height: 34,
                                  px: 0.9,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  borderRadius: "8px",
                                  background: "linear-gradient(180deg, rgba(255, 244, 238, 1), rgba(255, 247, 243, 1))",
                                  color: "#f97316",
                                  border: "1px solid rgba(255, 132, 76, 0.14)",
                                  fontSize: "0.8rem",
                                  fontWeight: 700,
                                }}
                              >
                                +{extra}
                              </Box>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "#4b5563",
                              fontWeight: 500,
                              wordBreak: "break-word",
                            }}
                          >
                            {p.id}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "#4b5563",
                              fontWeight: 500,
                              wordBreak: "break-word",
                            }}
                          >
                            {p.documentId ?? "--"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {p.productType ? (
                            <Chip
                              label={p.productType}
                              size="small"
                              sx={{
                                height: 28,
                                borderRadius: "999px",
                                backgroundColor: "rgba(255,255,255,0.9)",
                                border: "1px solid rgba(192, 38, 211, 0.55)",
                                color: "#c026d3",
                                fontWeight: 700,
                                fontSize: "0.75rem",
                                textTransform: "lowercase",
                              }}
                            />
                          ) : (
                            <Typography variant="body2" sx={{ color: "#9ca3af", fontWeight: 500 }}>
                              --
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={p.scraped ? "true" : "false"}
                            size="small"
                            sx={{
                              height: 24,
                              borderRadius: "999px",
                              backgroundColor: p.scraped ? "rgba(34, 197, 94, 0.08)" : "rgba(239, 68, 68, 0.08)",
                              border: `1px solid ${p.scraped ? "rgba(34, 197, 94, 0.4)" : "rgba(239, 68, 68, 0.4)"}`,
                              color: p.scraped ? "#16a34a" : "#dc2626",
                              fontWeight: 700,
                              fontSize: "0.72rem",
                            }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center", flexWrap: "wrap", maxWidth: { xs: 84, lg: 172 }, mx: "auto" }}>
                            <Tooltip title="Open in new tab" arrow>
                              <IconButton
                                size="small"
                                disabled={!p.link}
                                onClick={() => { if (p.link) window.open(p.link, "_blank", "noopener,noreferrer"); }}
                                sx={{
                                  width: 40,
                                  height: 40,
                                  borderRadius: "12px",
                                  color: "#374151",
                                  border: "1px solid rgba(15, 23, 42, 0.08)",
                                  backgroundColor: "rgba(255,255,255,0.88)",
                                  boxShadow: "0 10px 22px rgba(15, 23, 42, 0.06)",
                                  "&:hover": {
                                    backgroundColor: "#fff7f1",
                                    color: "#f97316",
                                  },
                                }}
                              >
                                <VisibilityOutlined fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="View scraped data" arrow>
                              <IconButton
                                size="small"
                                onClick={() => handleViewScrapedData(p.id, p.title)}
                                disabled={scrapedDataLoading === p.id}
                                sx={{
                                  width: 40,
                                  height: 40,
                                  borderRadius: "12px",
                                  color: "#16a34a",
                                  border: "1px solid rgba(34, 197, 94, 0.15)",
                                  backgroundColor: "rgba(255,255,255,0.88)",
                                  boxShadow: "0 10px 22px rgba(15, 23, 42, 0.06)",
                                  "&:hover": {
                                    backgroundColor: "rgba(34, 197, 94, 0.06)",
                                    color: "#15803d",
                                  },
                                }}
                              >
                                {scrapedDataLoading === p.id ? <CircularProgress size={16} color="inherit" /> : <TravelExploreOutlined fontSize="small" />}
                              </IconButton>
                            </Tooltip>
                            {hasFullAdmin && (
                            <Tooltip title="Edit product" arrow>
                              <IconButton
                                size="small"
                                onClick={() => handleOpenEditProduct(p)}
                                sx={{
                                  width: 40,
                                  height: 40,
                                  borderRadius: "12px",
                                  color: "#7c3aed",
                                  border: "1px solid rgba(124, 58, 237, 0.15)",
                                  backgroundColor: "rgba(255,255,255,0.88)",
                                  boxShadow: "0 10px 22px rgba(15, 23, 42, 0.06)",
                                  "&:hover": {
                                    backgroundColor: "rgba(124, 58, 237, 0.06)",
                                    color: "#6d28d9",
                                  },
                                }}
                              >
                                <EditOutlined fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            )}
                            {isAdmin && (
                            <Tooltip title="Delete product" arrow>
                              <IconButton
                                size="small"
                                onClick={() => setDeleteConfirmProduct(p)}
                                disabled={deletingProductId === p.id}
                                sx={{
                                  width: 40,
                                  height: 40,
                                  borderRadius: "12px",
                                  color: "#ef4444",
                                  border: "1px solid rgba(239, 68, 68, 0.15)",
                                  backgroundColor: "rgba(255,255,255,0.88)",
                                  boxShadow: "0 10px 22px rgba(15, 23, 42, 0.06)",
                                  "&:hover": {
                                    backgroundColor: "rgba(239, 68, 68, 0.06)",
                                    color: "#dc2626",
                                  },
                                }}
                              >
                                {deletingProductId === p.id ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlined fontSize="small" />}
                              </IconButton>
                            </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {totalPages > 1 && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: 2.5,
                  mt: 2.25,
                }}
              >
                <IconButton
                  size="small"
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  sx={{
                    width: 42,
                    height: 42,
                    borderRadius: "14px",
                    bgcolor: "rgba(255,255,255,0.86)",
                    border: "1px solid rgba(255, 132, 76, 0.14)",
                    boxShadow: "0 10px 22px rgba(236, 123, 55, 0.08)",
                    color: "#6b7280",
                    "&:hover": { bgcolor: "#fff", color: "#f97316" },
                    "&.Mui-disabled": {
                      bgcolor: "rgba(255,255,255,0.62)",
                      color: "rgba(107, 114, 128, 0.35)",
                    },
                  }}
                >
                  <ChevronLeft />
                </IconButton>
                <Typography variant="body2" sx={{ color: "#111827", fontWeight: 700 }}>
                  {start} - {end} of {total}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => setPage(page + 1)}
                  disabled={page === totalPages}
                  sx={{
                    width: 42,
                    height: 42,
                    borderRadius: "14px",
                    bgcolor: "rgba(255,255,255,0.86)",
                    border: "1px solid rgba(255, 132, 76, 0.14)",
                    boxShadow: "0 10px 22px rgba(236, 123, 55, 0.08)",
                    color: "#6b7280",
                    "&:hover": { bgcolor: "#fff", color: "#f97316" },
                    "&.Mui-disabled": {
                      bgcolor: "rgba(255,255,255,0.62)",
                      color: "rgba(107, 114, 128, 0.35)",
                    },
                  }}
                >
                  <ChevronRight />
                </IconButton>
              </Box>
            )}
          </Box>
        </Fade>
      )}

      {!loadingProducts && tenantId && products.length === 0 && (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          mt={4}
          mb={4}
        >
          <StorefrontOutlined sx={{ fontSize: 60, color: "#cbd5e1", mb: 1 }} />
          <Typography variant="body1" sx={{ color: "#4b5563", fontWeight: 700 }}>
            No products found
          </Typography>
          <Typography variant="caption" sx={{ color: "#9ca3af", mt: 0.5 }}>
            Try adjusting your search or filters
          </Typography>
        </Box>
      )}
      </Box>

      <Dialog
        open={openJson}
        onClose={() => setOpenJson(false)}
        maxWidth="xl"
        fullWidth
        scroll="paper"
        PaperProps={{
          sx: {
            borderRadius: "28px",
            minHeight: "80vh",
            border: "1px solid rgba(255, 132, 76, 0.12)",
            background: "radial-gradient(circle at top center, rgba(255, 191, 154, 0.20), rgba(255,255,255,0.98) 38%), linear-gradient(180deg, rgba(255, 248, 243, 0.98), rgba(255,255,255,0.96))",
            boxShadow: "0 30px 70px rgba(236, 123, 55, 0.16)",
            overflow: "hidden",
          }
        }}
      >
        <DialogTitle
          sx={{
            m: 0,
            p: 1.5,
            bgcolor: "transparent",
            borderBottom: "1px solid rgba(255, 132, 76, 0.10)",
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "12px",
                  background: "linear-gradient(180deg, #ffb58d 0%, #ff8b47 100%)",
                  boxShadow: "0 14px 28px rgba(255, 139, 71, 0.24)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  overflow: "hidden",
                  flexShrink: 0,
                }}
              >
                {jsonProduct?.imageUrl ? (
                  <Box
                    component="img"
                    src={jsonProduct?.imageUrl}
                    alt={jsonProduct?.title}
                    sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                ) : (
                  <StorefrontOutlined sx={{ fontSize: 24 }} />
                )}
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 800, color: "#202531", lineHeight: 1.1 }}>
                  {jsonProduct?.title}
                </Typography>
                <Typography variant="body2" sx={{ color: "#6b7280", mt: 0.35 }}>
                  Product ID: {jsonProduct?.id}
                </Typography>
              </Box>
            </Box>
            <IconButton
              size="small"
              aria-label="close"
              onClick={() => setOpenJson(false)}
              sx={{
                width: 40,
                height: 40,
                borderRadius: "14px",
                color: "#6b7280",
                "&:hover": { bgcolor: "#fff7f1", color: "#f97316" }
              }}
            >
              <Close />
            </IconButton>
          </Box>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="product details tabs"
            sx={{
              mt: 1,
              minHeight: 36,
              "& .MuiTabs-indicator": {
                height: 3,
                borderRadius: "999px",
                background: "linear-gradient(90deg, #ff8a3d, #ff6d1b)",
              },
              "& .MuiTab-root": {
                fontWeight: 700,
                textTransform: "none",
                fontSize: "0.95rem",
                py: 0.75,
                px: 1.6,
                minHeight: 36,
                borderRadius: "14px",
                color: "#4b5563",
                "&.Mui-selected": { color: "#f97316" },
                "&:hover": { color: "#f97316", backgroundColor: "rgba(255, 247, 241, 0.72)" },
              }
            }}
          >
            <Tab label="Simplified View" />
            <Tab label="Full JSON" />
          </Tabs>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            px: 2.5,
            py: 1.5,
            bgcolor: "transparent",
            borderTop: 0,
            borderBottom: 0,
          }}
        >
          {tabValue === 0 && (
            <Box sx={{ display: "grid", gap: 2, alignItems: "stretch", gridTemplateColumns: { xs: "minmax(0, 1fr)", lg: "repeat(2, minmax(0, 1fr))" } }}>
              <Box sx={{ minWidth: 0 }}>
                <Card
                  elevation={0}
                  sx={{
                    p: 1.75,
                    pb: 1.25,
                    mb: 2,
                    borderRadius: "22px",
                    border: "1px solid rgba(255, 132, 76, 0.10)",
                    backgroundColor: "rgba(255,255,255,0.82)",
                    boxShadow: "0 18px 38px rgba(236, 123, 55, 0.07)",
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={700} mb={0.5} sx={{ display: "flex", alignItems: "center", minHeight: 40, lineHeight: 1.4, color: "#202531" }}>
                    Product Information
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5, flexWrap: "wrap" }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight={700}>ID</Typography>
                      <Typography variant="body2" fontFamily="monospace" sx={{ color: "#202531", fontWeight: 500 }}>{jsonProduct?.id}</Typography>
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 200 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={700}>Title</Typography>
                      <Typography variant="body2" sx={{ color: "#202531", fontWeight: 500 }}>{jsonProduct?.title}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight={700}>Price</Typography>
                      <Typography variant="h6" sx={{ color: "#f97316", fontWeight: 800 }}>{jsonProduct?.price}</Typography>
                    </Box>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={700}>Link</Typography>
                    <Typography
                      component="a"
                      href={jsonProduct?.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="caption"
                      sx={{
                        color: "#f97316",
                        textDecoration: "none",
                        wordBreak: "break-all",
                        display: "block",
                        "&:hover": { textDecoration: "underline" }
                      }}
                    >
                      {jsonProduct?.link}
                    </Typography>
                  </Box>
                </Card>

                <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", mb: 1.5 }}>
                  <Typography variant="subtitle2" fontWeight={800} sx={{ color: "#202531" }}>
                    Variants ({jsonProduct?.variants?.length || 0})
                  </Typography>
                  <DebouncedSearchInput open={openJson} onSearch={setVariantSearchTerm} />
                  {variantSearchTerm && (
                    <Typography variant="caption" color="text.secondary" role="status">
                      {filteredVariants.length} of {jsonProduct?.variants?.length || 0} variants
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', flexDirection: matchingVariant ? 'column' : 'row', alignItems: 'flex-start', gap: 1.5 }}>
                  {variantSearchTerm && filteredVariants.length === 0 && (
                    <Typography variant="body2" color="text.secondary">No variants match your search.</Typography>
                  )}
                  {filteredVariants.map((v, i) => (
                    <Card
                      key={i}
                      elevation={0}
                      onClick={() => {
                        setVariantJson(v.raw);
                        setOpenVariantJson(true);
                      }}
                      sx={{
                        width: 160,
                        borderRadius: "18px",
                        border: "1px solid rgba(255, 132, 76, 0.10)",
                        backgroundColor: "rgba(255,255,255,0.84)",
                        boxShadow: "0 18px 38px rgba(236, 123, 55, 0.07)",
                        cursor: "pointer",
                        transition: "transform 0.2s, box-shadow 0.2s",
                        "&:hover": { transform: "translateY(-2px)", boxShadow: "0 22px 42px rgba(236, 123, 55, 0.10)" }
                      }}
                    >
                      <Box
                        component="img"
                        src={v.imageUrl}
                        alt={v.sku}
                        sx={{
                          width: '100%',
                          height: 120,
                          objectFit: 'cover',
                          borderTopLeftRadius: 6,
                          borderTopRightRadius: 6
                        }}
                      />
                      <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>SKU</Typography>
                        <Typography variant="body2" fontFamily="monospace" fontSize="0.75rem" mb={0.5} sx={{ color: "#202531", fontWeight: 500 }}>{v.sku}</Typography>

                        <Typography variant="caption" color="text.secondary" fontWeight={600}>Title</Typography>
                        <Typography variant="body2" fontSize="0.75rem" mb={0.5} sx={{
                          color: "#202531",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical"
                        }}>
                          {v.title}
                        </Typography>

                        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                          <Box>
                            <Typography variant="caption" color="text.secondary" fontWeight={600}>Price</Typography>
                            <Typography variant="body2" fontSize="0.75rem" fontWeight={800} sx={{ color: "#f97316" }}>{v.price}</Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary" fontWeight={600}>Stock</Typography>
                            <Typography variant="body2" fontSize="0.75rem" fontWeight={700} sx={{ color: "#202531" }}>{v.stock}</Typography>
                          </Box>
                        </Box>

                        {(v.displaySize || v.displayColor) && (
                          <Box sx={{ display: "flex", gap: 0.5, mb: 0.5, flexWrap: "wrap" }}>
                            {v.displaySize && <Chip label={v.displaySize} size="small" variant="outlined" sx={{ height: 18, fontSize: "0.65rem" }} />}
                            {v.displayColor && <Chip label={v.displayColor} size="small" variant="outlined" sx={{ height: 18, fontSize: "0.65rem" }} />}
                          </Box>
                        )}

                        {v.link && (
                          <Typography
                            component="a"
                            href={v.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="caption"
                            onClick={(e) => e.stopPropagation()}
                            sx={{
                              color: '#f97316',
                              textDecoration: 'none',
                              fontWeight: 700,
                              display: "block",
                              textAlign: "center",
                              mt: 0.5,
                              "&:hover": { textDecoration: "underline" }
                            }}
                          >
                            View Product →
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                  {matchingVariant && (
                    <Card
                      component="section"
                      aria-label="Matching variant JSON"
                      elevation={0}
                      sx={{
                        width: "100%",
                        minWidth: 0,
                        maxWidth: "100%",
                        borderRadius: "18px",
                        overflow: "hidden",
                        border: "1px solid #efddcf",
                        boxShadow: "0 18px 38px rgba(236, 123, 55, 0.07)",
                      }}
                    >
                      <Box sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.25,
                        p: 1.75,
                        background: "linear-gradient(135deg, #fdf5ee, #faece0)",
                        borderBottom: "1px solid #efddcf",
                      }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#513725", lineHeight: 1.4 }}>
                          Variant JSON
                          {matchingVariant.id != null && (
                            <Box component="span" sx={{ ml: 1, fontWeight: 500, color: "#6d4c36", overflowWrap: "anywhere" }}>
                              ID: {matchingVariant.id}
                            </Box>
                          )}
                        </Typography>
                      </Box>
                      <Box component="pre" sx={{
                        backgroundColor: "#fffaf7",
                        p: 1.5,
                        m: 0,
                        fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                        fontSize: "0.75rem",
                        whiteSpace: "pre-wrap",
                        overflowWrap: "anywhere",
                        maxHeight: "60vh",
                        overflow: "auto",
                      }}>
                        <ColoredJSONVariants data={matchingVariant.raw} searchTerm={variantSearchTerm} />
                      </Box>
                    </Card>
                  )}
                </Box>
              </Box>
              <Box sx={{ minWidth: 0, position: "relative", minHeight: { lg: 320 } }}>
                <ProductJSONPanel product={jsonProduct} open={openJson} fillHeight />
              </Box>
            </Box>
          )}
          {tabValue === 1 && (
            <ProductJSONPanel product={jsonProduct} open={openJson} fullView />
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={openVariantJson}
        onClose={() => setOpenVariantJson(false)}
        maxWidth="md"
        fullWidth
        scroll="paper"
        PaperProps={{
          sx: {
            borderRadius: "20px",
            border: "1px solid rgba(255, 132, 76, 0.12)",
            background: "linear-gradient(180deg, rgba(255, 248, 243, 0.98), rgba(255,255,255,0.96))",
            boxShadow: "0 30px 70px rgba(236, 123, 55, 0.16)",
          },
        }}
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid rgba(255, 132, 76, 0.10)" }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#202531" }}>
              Variant JSON Data
            </Typography>
            <Typography variant="body2" sx={{ color: "#6b7280", mt: 0.25 }}>
              SKU: {variantJson?.Sku || variantJson?.sku || "N/A"}
            </Typography>
          </Box>
          <IconButton
            onClick={() => setOpenVariantJson(false)}
            sx={{ color: "#6b7280", "&:hover": { backgroundColor: "#fff7f1", color: "#f97316" } }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ p: 2 }}>
            <Card
              elevation={0}
              sx={{
                borderRadius: "16px",
                overflow: "hidden",
                border: "1px solid rgba(255, 132, 76, 0.10)",
                boxShadow: "0 18px 38px rgba(236, 123, 55, 0.07)",
              }}
            >
              <Box component="pre" sx={{
                backgroundColor: '#fffaf7',
                p: 2,
                fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                fontSize: '0.75rem',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
                maxHeight: '60vh',
                overflow: 'auto',
                m: 0
              }}>
                <ColoredJSONVariants data={variantJson} />
              </Box>
            </Card>
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!deleteConfirmProduct}
        onClose={() => setDeleteConfirmProduct(null)}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            border: "1px solid rgba(239, 68, 68, 0.15)",
            p: 1,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: "#202531" }}>Delete Product</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: "#6b7280" }}>
            Are you sure you want to delete <strong>{deleteConfirmProduct?.title}</strong> (ID: {deleteConfirmProduct?.id})?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteConfirmProduct(null)} sx={{ color: "#6b7280" }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => handleDeleteProduct(deleteConfirmProduct?.id)}
            disabled={deletingProductId === deleteConfirmProduct?.id}
            sx={{
              backgroundColor: "#ef4444",
              "&:hover": { backgroundColor: "#dc2626" },
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 700,
            }}
          >
            {deletingProductId === deleteConfirmProduct?.id ? <CircularProgress size={16} color="inherit" /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!editProduct}
        onClose={() => { if (!savingProduct) setEditProduct(null); }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "20px",
            border: "1px solid rgba(124, 58, 237, 0.12)",
            background: "linear-gradient(180deg, rgba(255, 248, 243, 0.98), rgba(255,255,255,0.96))",
            boxShadow: "0 30px 70px rgba(124, 58, 237, 0.12)",
          },
        }}
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid rgba(124, 58, 237, 0.10)" }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#202531" }}>
              Edit Product
            </Typography>
            <Typography variant="body2" sx={{ color: "#6b7280", mt: 0.25 }}>
              {editProduct?.title} <span style={{ opacity: 0.6 }}>({editProduct?.id})</span>
            </Typography>
          </Box>
          <IconButton
            onClick={() => setEditProduct(null)}
            disabled={savingProduct}
            sx={{ color: "#6b7280", "&:hover": { backgroundColor: "#fff7f1", color: "#f97316" } }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5, pb: 1 }}>
          <Typography variant="body2" sx={{ color: "#6b7280", mb: 1.5 }}>
            Enter the fields to update as a JSON object. Only include the fields you want to change.
          </Typography>
          <TextField
            multiline
            fullWidth
            minRows={16}
            maxRows={28}
            value={editFields}
            onChange={(e) => { setEditFields(e.target.value); setEditFieldsError(""); }}
            error={!!editFieldsError}
            helperText={editFieldsError}
            placeholder={'{\n  "Title": "New title",\n  "Price": 29.99\n}'}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                fontSize: "0.82rem",
                backgroundColor: "#fffaf7",
                "& fieldset": { borderColor: "rgba(124, 58, 237, 0.20)" },
                "&:hover fieldset": { borderColor: "#7c3aed" },
                "&.Mui-focused fieldset": { borderColor: "#7c3aed" },
              },
            }}
          />
          {editFeedback && (
            <Box
              sx={{
                mt: 1.5,
                px: 1.5,
                py: 1,
                borderRadius: "10px",
                backgroundColor: editFeedback.type === "success" ? "rgba(34, 197, 94, 0.08)" : "rgba(239, 68, 68, 0.08)",
                border: `1px solid ${editFeedback.type === "success" ? "rgba(34, 197, 94, 0.22)" : "rgba(239, 68, 68, 0.22)"}`,
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.84rem", color: editFeedback.type === "success" ? "#15803d" : "#b91c1c" }}>
                {editFeedback.message}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setEditProduct(null)}
            disabled={savingProduct}
            sx={{ color: "#6b7280", textTransform: "none", fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveProduct}
            disabled={savingProduct}
            startIcon={savingProduct ? <CircularProgress size={15} sx={{ color: "rgba(255,255,255,0.8)" }} /> : null}
            sx={{
              backgroundColor: "#7c3aed",
              "&:hover": { backgroundColor: "#6d28d9" },
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 700,
            }}
          >
            {savingProduct ? "Saving…" : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={scrapedDataOpen}
        onClose={() => setScrapedDataOpen(false)}
        maxWidth="md"
        fullWidth
        scroll="paper"
        PaperProps={{
          sx: {
            borderRadius: "20px",
            border: "1px solid rgba(255, 132, 76, 0.12)",
            background: "linear-gradient(180deg, rgba(255, 248, 243, 0.98), rgba(255,255,255,0.96))",
            boxShadow: "0 30px 70px rgba(236, 123, 55, 0.16)",
          },
        }}
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid rgba(255, 132, 76, 0.10)" }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#202531" }}>
              Scraped Product Data
            </Typography>
            <Typography variant="body2" sx={{ color: "#6b7280", mt: 0.25 }}>
              Product Title: <strong>{scrapedDataProductName}</strong>
            </Typography>
            <Typography variant="body2" sx={{ color: "#6b7280", mt: 0.25 }}>
              Product ID: <strong>{scrapedDataProductId}</strong> • Index: <strong>{currentTenant?.webScrapeIndexName || currentTenant?.tenantAiProfile?.webScrapeIndexName || "N/A"}</strong>
            </Typography>
          </Box>
          <IconButton onClick={() => setScrapedDataOpen(false)} sx={{ color: "#6b7280", "&:hover": { backgroundColor: "#fff7f1", color: "#f97316" } }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {scrapedDataJson?._error ? (
            <Box sx={{ p: 3 }}>
              <Typography variant="body1" sx={{ color: "#dc2626", fontWeight: 600 }}>
                {scrapedDataJson._error}
              </Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ p: 2, pb: 1, borderBottom: "1px solid rgba(255, 132, 76, 0.10)", display: "flex", gap: 1.5, alignItems: "center" }}>
                <TextField
                  size="small"
                  placeholder="Search in scraped data..."
                  value={scrapedDataSearch}
                  onChange={(e) => setScrapedDataSearch(e.target.value)}
                  sx={{
                    flex: 1,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      backgroundColor: "#fffaf7",
                      "& fieldset": { borderColor: "rgba(255, 132, 76, 0.20)" },
                      "&:hover fieldset": { borderColor: "#f97316" },
                      "&.Mui-focused fieldset": { borderColor: "#f97316" },
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchOutlined sx={{ color: "#f97316" }} />
                      </InputAdornment>
                    ),
                  }}
                />
                <Tabs
                  value={scrapedDataView}
                  onChange={(_, v) => setScrapedDataView(v)}
                  sx={{
                    minHeight: 36,
                    "& .MuiTabs-indicator": { height: 2, borderRadius: "999px", backgroundColor: "#f97316" },
                    "& .MuiTab-root": {
                      minHeight: 36,
                      py: 0.5,
                      px: 1.5,
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      textTransform: "none",
                      color: "#6b7280",
                      "&.Mui-selected": { color: "#f97316" },
                    },
                  }}
                >
                  <Tab value="compact" label="Compact" />
                  <Tab value="formatted" label="Formatted" />
                </Tabs>
              </Box>
              <Box component="pre" sx={{
                backgroundColor: '#fffaf7',
                p: 2.5,
                fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                fontSize: '0.78rem',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
                maxHeight: '70vh',
                overflow: 'auto',
                m: 0,
                lineHeight: 1.6,
              }}>
                {scrapedDataView === "formatted" ? (
                  <CollapsibleJSON data={scrapedDataJson} searchTerm={scrapedDataSearch} />
                ) : (
                  (() => {
                    const json = JSON.stringify(scrapedDataJson, null, 2);
                    if (!scrapedDataSearch.trim()) return json;
                    const parts = json.split(new RegExp(`(${scrapedDataSearch.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'));
                    return parts.map((part, i) =>
                      part.toLowerCase() === scrapedDataSearch.toLowerCase()
                        ? <mark key={i} style={{ backgroundColor: "rgba(249, 115, 22, 0.3)", color: "#202531", borderRadius: "2px", padding: "0 2px" }}>{part}</mark>
                        : part
                    );
                  })()
                )}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setScrapedDataOpen(false)}
            sx={{
              color: "#f97316",
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 700,
              "&:hover": { backgroundColor: "#fff7f1" },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

ProductsTab.propTypes = { tenants: PropTypes.array, selectedTenant: PropTypes.string, setSelectedTenant: PropTypes.func };
export default ProductsTab;
